import { type KeyboardEvent, useEffect, useState } from "react";
import { fmt } from "../../format";
import { Icon, type IconName } from "../../icons";
import { tabIndexAfterKey } from "./tabKeys";
import { useSlidingIndicator } from "./useSlidingIndicator";

export interface TabItem {
  id: string;
  label: string;
  count?: number;
  icon?: IconName;
}

export interface TabsProps {
  items: TabItem[];
  value?: string;
  onChange?: (id: string) => void;
  /** Stretch tabs to fill the width. */
  block?: boolean;
  ariaLabel?: string;
}

/** Segmented tabs with a sliding indicator and arrow-key navigation. */
export function Tabs({ items, value, onChange, block, ariaLabel }: TabsProps): React.JSX.Element {
  const [current, setCurrent] = useState(value ?? items[0]?.id ?? "");
  const { indicatorRef, listRef, buttonRefs } = useSlidingIndicator(current, items);

  useEffect(() => {
    if (value !== undefined) setCurrent(value);
  }, [value]);

  function select(id: string): void {
    setCurrent(id);
    onChange?.(id);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    const next = tabIndexAfterKey(e.key, items.findIndex((i) => i.id === current), items.length);
    if (next < 0) return;
    e.preventDefault();
    const item = items[next];
    if (!item) return;
    select(item.id);
    buttonRefs.current[item.id]?.focus();
  }

  return (
    <div ref={listRef} className={`nb-tabs${block ? " nb-tabs-block" : ""}`} role="tablist" aria-label={ariaLabel} onKeyDown={onKeyDown}>
      <span className="nb-tabs-ind" aria-hidden="true" ref={indicatorRef} />
      {items.map((item) => (
        <button
          key={item.id}
          ref={(el) => {
            buttonRefs.current[item.id] = el;
          }}
          className="nb-tab"
          role="tab"
          type="button"
          tabIndex={item.id === current ? 0 : -1}
          aria-selected={item.id === current}
          onClick={() => select(item.id)}
        >
          {item.icon ? <Icon name={item.icon} /> : null}
          {item.label}
          {item.count === undefined ? null : <span className="nb-tab-n">{fmt.number(item.count)}</span>}
        </button>
      ))}
    </div>
  );
}
